// src/App.tsx
import React, { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

// --- Core App Components ---
import { FirebaseLoginButton } from './components/FirebaseLoginButton';
import FutureSelfChat from './components/FutureSelfChat';
import { ConsentScreen } from './components/ConsentScreen';

import { MainMenu } from './components/MainMenu';
import { ProfileMenu } from './components/ProfileMenu';

// --- Services & Logic ---
import { firebaseAuthService, FirebaseUser } from './services/firebaseAuth.service';
import { userProfileService, loadProfileSafe, saveProfileSafe, createSkeletonProfile as createSkeletonProfileSafe } from './services/userProfileService';
import { User, UserProfile, GameState } from './types';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import NewUIRoot from "@/newui/Root";
import { useProgressiveProfile } from './hooks/useProgressiveProfile';
import { ColonyProvider } from '../src/contexts/ColonyContext';

// Guard against legacy global calls that may exist in compiled bundles
// This prevents crashes like "Can't find variable: clearHistory"
(globalThis as any).clearHistory = (globalThis as any).clearHistory || (() => {});

const queryClient = new QueryClient();

type AppScreen = 'auth' | 'consent' | 'chat' | 'main-menu';

function App() {
  const [currentScreen, setCurrentScreen] = useState<AppScreen>('auth');
  const [loading, setLoading] = useState(true);
  const [authMessage, setAuthMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [gameState, setGameState] = useState<GameState>({
    user: null,
    currentProfile: null,
    isLoading: false
  });

  // ===================================================================
  // Hides the keyboard accessory bar for a native feel
  // ===================================================================
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      Keyboard.setAccessoryBarVisible({ isVisible: false });
    }
  }, []);
  // ===================================================================

  // Helper for consent persistence
  const getConsentKey = (uid: string) => `consent:${uid}`;

  // --- Firebase Auth State Listener ---
  useEffect(() => {
    const unsubscribe = firebaseAuthService.onAuthStateChanged((user) => {
      console.log('[App] Auth state changed:', user ? { uid: user.uid, email: user.email } : null);
      
      if (!user) {
        // User signed out - clear state and go to auth screen
        console.log('[App] User signed out, clearing state');
        localStorage.removeItem('lotteryOfLife_user');
        localStorage.removeItem('lotteryOfLife_userProfile');
        setGameState({ user: null, currentProfile: null, isLoading: false });
        setCurrentScreen('auth');
        setAuthMessage(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // --- Authentication & Initialization ---
  useEffect(() => {
    const initializeApp = async () => {
      try {
        setLoading(true);
        const currentUser = await firebaseAuthService.getCurrentUser();
        
        if (currentUser) {
          const savedUserStr = localStorage.getItem('lotteryOfLife_user');
          let savedUser: User | null = null;
          
          if (savedUserStr) {
            try {
              const parsedUser = JSON.parse(savedUserStr);
              if (parsedUser.id === currentUser.uid) savedUser = parsedUser;
            } catch (e) { 
              console.error("Failed to parse saved user data", e); 
            }
          }

          // Read persisted consent flag (survives rebuilds)
          const consentAccepted = localStorage.getItem(getConsentKey(currentUser.uid)) === 'true';
          
          // For existing users (created before consent was required), assume consent was given
          const isExistingUser = currentUser.metadata?.creationTime && 
                                 (Date.now() - new Date(currentUser.metadata.creationTime).getTime()) > 24 * 60 * 60 * 1000; // 1 day old

          const user: User = {
            id: currentUser.uid,
            email: currentUser.email || '',
            name: currentUser.displayName || currentUser.email || '',
            hasCompletedOnboarding: savedUser?.hasCompletedOnboarding || consentAccepted || isExistingUser || false,
            subscriptionTier: 'free',
            createdAt: new Date(currentUser.metadata?.creationTime || Date.now()),
          };

          localStorage.setItem('lotteryOfLife_user', JSON.stringify(user));
          
          // Safe profile bootstrap (never throw on corrupt JSON)
          let profile: any = loadProfileSafe();
          if (!profile) {
            // Create once silently without noisy log
            profile = saveProfileSafe(createSkeletonProfileSafe({ uid: currentUser.uid, email: currentUser.email }));
          }
          // Absolute fallback
          if (!profile) {
            profile = saveProfileSafe(createSkeletonProfileSafe({ uid: 'anon' }));
          }

          setGameState({ user, currentProfile: profile, isLoading: false });

          // Bootstrap Prime Colony + Alt-me on sign-in (idempotent)
          try {
            const { api } = await import('../src/lib/api/client');
            await api.getColonies();
          } catch (e) {
            console.debug('[App] Bootstrap colonies failed (non-fatal):', (e as any)?.message || e);
          }

          // Check if user has completed onboarding (including consent)
          if (user.hasCompletedOnboarding) {
            setCurrentScreen('chat');
          } else {
            setCurrentScreen('consent'); // Skip welcome, go directly to consent
          }

        } else {
          setGameState({ user: null, currentProfile: null, isLoading: false });
          setCurrentScreen('auth');
        }
      } catch (error) {
        console.error('Failed to initialize app:', error);
        setGameState({ user: null, currentProfile: null, isLoading: false });
        setCurrentScreen('auth');
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, []);

  // --- Event Handlers ---

  const handleAuthSuccess = async (firebaseUser: FirebaseUser) => {
    try {
      console.log('[App] handleAuthSuccess called with user:', firebaseUser);
      
      const savedConsent = localStorage.getItem(getConsentKey(firebaseUser.uid)) === 'true';
      const user: User = {
        id: firebaseUser.uid,
        email: firebaseUser.email || '',
        name: firebaseUser.displayName || firebaseUser.email || '',
        hasCompletedOnboarding: !!savedConsent,
        subscriptionTier: 'free',
        createdAt: new Date(), // Fixed: Just use current date
      };

      console.log('[App] Created user object:', user);
      localStorage.setItem('lotteryOfLife_user', JSON.stringify(user));
      
      // Safe profile bootstrap on new login
      let profile: any = loadProfileSafe();
      if (!profile) {
          // Silent bootstrap on first login
          profile = saveProfileSafe(createSkeletonProfileSafe({ uid: firebaseUser.uid, email: firebaseUser.email }));
      }
      
      setGameState({ user, currentProfile: profile, isLoading: false });

      // Bootstrap Prime Colony + Alt-me on new login (idempotent)
      try {
        const { api } = await import('../src/lib/api/client');
        await api.getColonies();
      } catch (e) {
        console.debug('[App] Bootstrap colonies failed (non-fatal):', (e as any)?.message || e);
      }

      setCurrentScreen(user.hasCompletedOnboarding ? 'chat' : 'consent');
      
      setAuthMessage({ type: 'success', text: 'Successfully logged in!' });
    } catch (error) {
      console.error('[App] Auth success handler error:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setAuthMessage({ type: 'error', text: 'Failed to initialize user profile: ' + errorMessage });
      // Don't rethrow - let the user see the error message
    }
  };

  const handleAuthError = (error: Error) => {
    console.error('Authentication error:', error);
    setAuthMessage({ type: 'error', text: error.message });
  };

  const handleSignOut = async () => {
    try {
      await firebaseAuthService.signOut();
      localStorage.removeItem('lotteryOfLife_user');
      setGameState({ user: null, currentProfile: null, isLoading: false });
      setCurrentScreen('auth');
      setAuthMessage(null);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handlePasswordReset = async (email: string) => {
      if (!email) {
          setAuthMessage({type: 'error', text: "Please enter your email to reset your password."});
          return;
      }
      try {
          await firebaseAuthService.sendPasswordResetEmail(email);
          setAuthMessage({type: 'success', text: "Password reset email sent! Please check your inbox and spam folder."});
      } catch (error) {
          console.error("Password reset error:", error);
          setAuthMessage({type: 'error', text: (error as Error).message});
      }
  }


  
  const handleConsent = () => {
    // Mark onboarding as completed when consent is given
    if (gameState.user) {
      const updatedUser = { ...gameState.user, hasCompletedOnboarding: true };
      localStorage.setItem('lotteryOfLife_user', JSON.stringify(updatedUser));
      // Persist separate consent flag as well (more robust)
      localStorage.setItem(getConsentKey(gameState.user.id), 'true');
      setGameState({ ...gameState, user: updatedUser });
    }
    setCurrentScreen('chat');
  };
  
  const handleConsentBack = () => setCurrentScreen('auth');
  const handleStartChat = () => setCurrentScreen('chat');

  const { profile, updateProfile, isSaving } = useProgressiveProfile(gameState.currentProfile);

  const renderLoadingScreen = () => (
    <div className="mobile-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );

  const renderAuthScreen = () => (
    <div className="mobile-screen flex items-center justify-center bg-background mobile-content p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-foreground title-primary mb-2">BrainColonies</h1>
          <p className="text-muted-foreground text-contemplative">AI-Powered Collaborative Intelligence</p>
        </div>
        
        <FirebaseLoginButton 
          onAuthSuccess={handleAuthSuccess}
          onAuthError={handleAuthError}
          onPasswordReset={handlePasswordReset}
        />
        
        {authMessage && (
          <div className={`p-4 rounded-lg ${
            authMessage.type === 'success' 
              ? 'bg-accent/10 text-accent-foreground border border-accent/20' 
              : 'bg-destructive/10 text-destructive-foreground border border-destructive/20'
          }`}>
            {authMessage.text}
          </div>
        )}
      </div>
    </div>
  );

  const renderScreen = () => {
    if (loading || (gameState.isLoading && currentScreen !== 'chat')) return renderLoadingScreen();
    switch (currentScreen) {
      case 'auth': return renderAuthScreen();
      case 'consent': return <ConsentScreen profile={gameState.currentProfile} onConsent={handleConsent} onBack={handleConsentBack} />;
      case 'main-menu': return <MainMenu user={gameState.user!} onLogout={handleSignOut} onStartChat={handleStartChat} />;
      case 'chat':
        return <NewUIRoot />;
      default: return renderAuthScreen();
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ColonyProvider>
        <div className="App">{renderScreen()}</div>
      </ColonyProvider>
    </QueryClientProvider>
  );
}

export default App;
